import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, MessageSquare, BarChart3, User, Calendar, Globe, TrendingUp, Users, Zap, AlertTriangle, Sparkles, Heart, Briefcase, Award, Clock, Skull } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";

// Timeline Milestone Component
const TimelineMilestone = ({ milestone, isLast, currentYear }) => {
  const getCategoryIcon = (category) => {
    switch (category) {
      case "career": return <Briefcase className="w-3 h-3" />;
      case "family": return <Heart className="w-3 h-3" />;
      case "health": return <Sparkles className="w-3 h-3" />;
      case "achievement": return <Award className="w-3 h-3" />;
      case "loss": return <Skull className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };
  
  const getCategoryColor = (category, impact) => {
    if (category === "loss") return "bg-gray-600 border-gray-500";
    switch (impact) {
      case "positive": return "bg-green-600 border-green-500";
      case "negative": return "bg-red-600 border-red-500";
      default: return "bg-blue-600 border-blue-500";
    }
  };
  
  const getLineColor = (impact) => {
    if (milestone.category === "loss") return "bg-gray-600";
    switch (impact) {
      case "positive": return "bg-green-600/50";
      case "negative": return "bg-red-600/50";
      default: return "bg-blue-600/50";
    }
  };

  return (
    <div className="flex items-start gap-3 relative">
      {/* Vertical line */}
      {!isLast && (
        <div className={`absolute left-[11px] top-6 w-0.5 h-full ${getLineColor(milestone.impact)}`} />
      )}
      
      {/* Milestone dot */}
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${getCategoryColor(milestone.category, milestone.impact)} z-10`}>
        {getCategoryIcon(milestone.category)}
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-white">{milestone.year}</span>
          <span className="text-xs text-zinc-500">Age {milestone.age}</span>
          {milestone.year <= currentYear && (
            <span className="text-[10px] text-zinc-600 bg-zinc-800 rounded px-1">Past</span>
          )}
        </div>
        <p className="text-sm text-zinc-300">{milestone.event}</p>
      </div>
    </div>
  );
};

// Persona Timeline Component  
const PersonaTimeline = ({ futureContext, currentYear }) => {
  const milestones = futureContext?.timeline_milestones || [];
  
  if (milestones.length === 0) return null;
  
  return (
    <div className="mt-3 p-3 bg-zinc-900/80 rounded-xl border border-white/10">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-yellow-400" />
        <span className="text-xs font-medium text-yellow-400">Life Timeline</span>
        {!futureContext.is_alive && (
          <span className="text-xs bg-gray-700 text-gray-300 rounded-full px-2 py-0.5 flex items-center gap-1">
            <Skull className="w-3 h-3" /> Deceased
          </span>
        )}
      </div>
      
      <div className="pl-1">
        {milestones.map((milestone, idx) => (
          <TimelineMilestone 
            key={idx} 
            milestone={milestone} 
            isLast={idx === milestones.length - 1}
            currentYear={currentYear}
          />
        ))}
      </div>
      
      {/* Legacy section for deceased */}
      {!futureContext.is_alive && futureContext.legacy && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <p className="text-xs text-zinc-500 mb-1">Legacy</p>
          <p className="text-sm text-zinc-300 italic">{futureContext.legacy}</p>
        </div>
      )}
    </div>
  );
};

export const QueryResultView = ({ result, personas, index, onPersonaClick }) => {
  const [isExpanded, setIsExpanded] = useState(index === 0);
  const [viewMode, setViewMode] = useState("summary"); // summary | individual | scenario
  const { t } = useLanguage();
  
  const currentYear = new Date().getFullYear();

  const getPersonaById = (id) => personas.find(p => p.id === id);
  
  const isFutureQuery = result.years_into_future > 0;
  const futureScenario = result.future_scenario;

  const getSentimentClass = (sentiment) => {
    switch (sentiment) {
      case "positive": return "sentiment-positive";
      case "negative": return "sentiment-negative";
      case "neutral": return "sentiment-neutral";
      case "mixed": return "sentiment-mixed";
      default: return "sentiment-neutral";
    }
  };
  
  const getScenarioColor = (type) => {
    switch (type) {
      case "optimistic": return "text-green-400 bg-green-500/20 border-green-500/50";
      case "pessimistic": return "text-red-400 bg-red-500/20 border-red-500/50";
      default: return "text-yellow-400 bg-yellow-500/20 border-yellow-500/50";
    }
  };

  const totalResponses = Object.values(result.sentiment_breakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      data-testid={`query-result-${result.id}`}
      className="glass rounded-2xl overflow-hidden"
    >
      {/* Header */}
      <div 
        className="p-5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-white font-medium">&ldquo;{result.question}&rdquo;</p>
              {/* Future Query Badge */}
              {isFutureQuery && (
                <span className={`text-xs rounded-full px-2 py-0.5 border flex items-center gap-1 ${getScenarioColor(result.scenario_type)}`}>
                  <Calendar className="w-3 h-3" />
                  {futureScenario?.target_year} • {t(`future.${result.scenario_type}`)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {result.sentiment_breakdown && (
                <>
                  {result.sentiment_breakdown.positive > 0 && (
                    <span className="sentiment-positive text-xs rounded-full px-3 py-1">
                      {Math.round((result.sentiment_breakdown.positive / totalResponses) * 100)}% {t("query.positive")}
                    </span>
                  )}
                  {result.sentiment_breakdown.neutral > 0 && (
                    <span className="sentiment-neutral text-xs rounded-full px-3 py-1">
                      {Math.round((result.sentiment_breakdown.neutral / totalResponses) * 100)}% {t("query.neutral")}
                    </span>
                  )}
                  {result.sentiment_breakdown.negative > 0 && (
                    <span className="sentiment-negative text-xs rounded-full px-3 py-1">
                      {Math.round((result.sentiment_breakdown.negative / totalResponses) * 100)}% {t("query.negative")}
                    </span>
                  )}
                  {result.sentiment_breakdown.mixed > 0 && (
                    <span className="sentiment-mixed text-xs rounded-full px-3 py-1">
                      {Math.round((result.sentiment_breakdown.mixed / totalResponses) * 100)}% {t("query.mixed")}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-white/5"
          >
            {/* View Mode Toggle */}
            <div className="p-4 border-b border-white/5 flex gap-2">
              {isFutureQuery && futureScenario && (
                <Button
                  variant={viewMode === "scenario" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("scenario")}
                  className={viewMode === "scenario" ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-black" : "text-zinc-400"}
                  data-testid="view-scenario-btn"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {t("future.scenarioDetails")}
                </Button>
              )}
              <Button
                variant={viewMode === "summary" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("summary")}
                className={viewMode === "summary" ? "bg-white text-black" : "text-zinc-400"}
                data-testid="view-summary-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {t("query.summary")}
              </Button>
              <Button
                variant={viewMode === "individual" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("individual")}
                className={viewMode === "individual" ? "bg-white text-black" : "text-zinc-400"}
                data-testid="view-individual-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {t("query.individual")} ({result.responses?.length || 0})
              </Button>
            </div>

            <div className="p-5">
              {/* Future Scenario View */}
              {viewMode === "scenario" && futureScenario && (
                <div className="space-y-6">
                  {/* World Overview */}
                  <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-xl p-5 border border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="w-5 h-5 text-yellow-400" />
                      <h4 className="font-medium text-white">{t("future.worldOverview")} {futureScenario.target_year}</h4>
                      <span className={`text-xs rounded-full px-2 py-0.5 border ${getScenarioColor(result.scenario_type)}`}>
                        {t(`future.${result.scenario_type}`)}
                      </span>
                    </div>
                    <p className="text-zinc-300 leading-relaxed text-sm whitespace-pre-line">{futureScenario.world_overview}</p>
                  </div>
                  
                  {/* Grid of scenario details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Economic Outlook */}
                    <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        <h5 className="text-sm font-medium text-white">{t("future.economicOutlook")}</h5>
                      </div>
                      <p className="text-zinc-400 text-sm">{futureScenario.economic_outlook}</p>
                    </div>
                    
                    {/* Environmental */}
                    <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-green-400" />
                        <h5 className="text-sm font-medium text-white">{t("future.environmentalFactors")}</h5>
                      </div>
                      <p className="text-zinc-400 text-sm">{futureScenario.environmental_factors}</p>
                    </div>
                    
                    {/* Political */}
                    <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-purple-400" />
                        <h5 className="text-sm font-medium text-white">{t("future.politicalLandscape")}</h5>
                      </div>
                      <p className="text-zinc-400 text-sm">{futureScenario.political_landscape}</p>
                    </div>
                    
                    {/* Technology Trends */}
                    <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        <h5 className="text-sm font-medium text-white">{t("future.technologyTrends")}</h5>
                      </div>
                      <ul className="space-y-1">
                        {futureScenario.technology_trends?.slice(0, 5).map((trend, i) => (
                          <li key={i} className="text-zinc-400 text-sm">• {trend}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Industry Disruptions */}
                  <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      <h5 className="text-sm font-medium text-white">{t("future.industryDisruptions")}</h5>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {futureScenario.industry_disruptions?.map((disruption, i) => (
                        <div key={i} className="text-zinc-400 text-sm bg-zinc-800/50 rounded-lg px-3 py-2">
                          {disruption}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Opportunities & Challenges */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                      <h5 className="text-sm font-medium text-green-400 mb-2">{t("future.keyOpportunities")}</h5>
                      <ul className="space-y-1">
                        {futureScenario.key_opportunities?.map((opp, i) => (
                          <li key={i} className="text-zinc-300 text-sm">✓ {opp}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                      <h5 className="text-sm font-medium text-red-400 mb-2">{t("future.keyChallenges")}</h5>
                      <ul className="space-y-1">
                        {futureScenario.key_challenges?.map((challenge, i) => (
                          <li key={i} className="text-zinc-300 text-sm">⚠ {challenge}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Consumer Behavior Shifts */}
                  <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                    <h5 className="text-sm font-medium text-white mb-3">{t("future.consumerBehavior")}</h5>
                    <div className="flex flex-wrap gap-2">
                      {futureScenario.consumer_behavior_shifts?.map((shift, i) => (
                        <span key={i} className="text-xs bg-zinc-800 text-zinc-300 rounded-full px-3 py-1 border border-white/10">
                          {shift}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {viewMode === "summary" ? (
                <div className="space-y-4">
                  {/* Summary Text */}
                  <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">{t("query.executiveSummary")}</h4>
                    <p className="text-zinc-300 leading-relaxed">{result.summary}</p>
                  </div>

                  {/* Key Themes */}
                  {result.key_themes && result.key_themes.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-zinc-400 mb-2">Key Themes</h4>
                      <div className="flex flex-wrap gap-2">
                        {result.key_themes.map((theme, i) => (
                          <span 
                            key={i}
                            className="px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-sm text-indigo-300"
                          >
                            {theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sentiment Breakdown Chart */}
                  {result.sentiment_breakdown && (
                    <div>
                      <h4 className="text-sm font-medium text-zinc-400 mb-3">Sentiment Breakdown</h4>
                      <div className="grid grid-cols-4 gap-3">
                        {Object.entries(result.sentiment_breakdown).map(([sentiment, count]) => (
                          <div 
                            key={sentiment}
                            className="bg-zinc-900/50 rounded-xl p-4 text-center"
                          >
                            <span className={`
                              inline-block w-3 h-3 rounded-full mb-2
                              ${sentiment === 'positive' ? 'bg-green-500' :
                                sentiment === 'negative' ? 'bg-red-500' :
                                sentiment === 'neutral' ? 'bg-zinc-500' :
                                'bg-yellow-500'}
                            `} />
                            <p className="text-2xl font-heading font-bold text-white">{count}</p>
                            <p className="text-xs text-zinc-500 capitalize">{sentiment}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : viewMode === "individual" ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {result.responses?.map((response) => {
                    const persona = getPersonaById(response.persona_id);
                    const futureContext = response.future_context;
                    const isDeceased = futureContext && !futureContext.is_alive;
                    
                    return (
                      <motion.div
                        key={response.persona_id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`response-card rounded-xl p-4 border ${isDeceased ? 'bg-zinc-900/30 border-zinc-700/50' : 'bg-zinc-900/50 border-white/5'}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Avatar - with deceased indicator */}
                          <div 
                            className={`w-12 h-12 rounded-full flex-shrink-0 overflow-hidden cursor-pointer hover:ring-2 transition-all relative ${isDeceased ? 'opacity-60 grayscale hover:ring-gray-500' : 'hover:ring-yellow-500'}`}
                            onClick={() => persona && onPersonaClick && onPersonaClick(persona)}
                            title={`View ${response.persona_name}'s profile`}
                          >
                            {persona?.avatar_base64 ? (
                              <img
                                src={`data:image/jpeg;base64,${persona.avatar_base64}`}
                                alt={response.persona_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                              </div>
                            )}
                            {isDeceased && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <Skull className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span 
                                  className={`font-medium cursor-pointer transition-colors ${isDeceased ? 'text-zinc-400 hover:text-zinc-300' : 'text-white hover:text-yellow-400'}`}
                                  onClick={() => persona && onPersonaClick && onPersonaClick(persona)}
                                >
                                  {response.persona_name}
                                </span>
                                {/* Life status badges */}
                                {futureContext && (
                                  <>
                                    {isDeceased ? (
                                      <span className="text-xs bg-gray-800 text-gray-400 rounded-full px-2 py-0.5 flex items-center gap-1">
                                        <Skull className="w-3 h-3" />
                                        {futureContext.death_year} (age {futureContext.death_age})
                                      </span>
                                    ) : (
                                      <span className="text-xs text-zinc-500 bg-zinc-800 rounded-full px-2 py-0.5">
                                        Age {futureContext.age_in_future} in {futureScenario?.target_year}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              <span className={`${getSentimentClass(response.sentiment)} text-xs rounded-full px-2 py-0.5`}>
                                {response.sentiment}
                              </span>
                            </div>
                            
                            {/* Response */}
                            <p className={`text-sm leading-relaxed ${isDeceased ? 'text-zinc-500 italic' : 'text-zinc-400'}`}>
                              {response.response}
                            </p>
                            
                            {/* Timeline Milestones - Cool Visual */}
                            {futureContext && (
                              <PersonaTimeline futureContext={futureContext} currentYear={currentYear} />
                            )}
                            
                            {/* Evolution Summary (collapsed by default) */}
                            {futureContext && !isDeceased && (
                              <details className="mt-3">
                                <summary className="text-xs font-medium text-yellow-400 cursor-pointer hover:text-yellow-300 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" />
                                  {t("future.personaEvolution")} Details
                                </summary>
                                <div className="mt-2 p-3 bg-zinc-800/50 rounded-lg border border-white/5">
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="text-zinc-500">{t("future.careerEvolution")}:</span>
                                      <p className="text-zinc-300 mt-0.5">{futureContext.career_evolution}</p>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">{t("future.financialSituation")}:</span>
                                      <p className="text-zinc-300 mt-0.5">{futureContext.financial_situation}</p>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">{t("future.valuesShift")}:</span>
                                      <p className="text-zinc-300 mt-0.5">{futureContext.values_shift}</p>
                                    </div>
                                    <div>
                                      <span className="text-zinc-500">{t("future.techAdoption")}:</span>
                                      <p className="text-zinc-300 mt-0.5">{futureContext.tech_adoption}</p>
                                    </div>
                                  </div>
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QueryResultView;
